/**
 * Entry routing for the loan-purpose split (WI-TV-W5-001, WI-TV-W6-001,
 * WI-TV-W65-001).
 *
 * Wave 5 introduced the refinance wizard while purchase stayed on the legacy
 * long form. Wave 6 reskins purchase, so both purposes now enter a step journey:
 * this module resolves which one to render at load from an untrusted
 * `loanPurpose` hint (query parameter).
 *
 * Routing rule (WI-TV-W65-001): only an explicit, normalized `purchase` or
 * `refinance` hint direct-routes to that journey's step 1. A missing, blank, or
 * unrecognized value routes to neither — it resolves to the two-option selector
 * so the borrower makes the choice, with no purpose preselected. `loanPurpose`
 * is routing-only: no other query parameter seeds purchase data.
 */

export type EntryMode = "refinance-wizard" | "purchase-wizard" | "selector";

/** Query parameter that selects the loan-purpose entry experience. */
export const LOAN_PURPOSE_QUERY_PARAM = "loanPurpose";

/**
 * Maps a raw, untrusted loan-purpose value to an {@link EntryMode}. Matching is
 * case-insensitive and whitespace-tolerant. Only an explicit `purchase` or
 * `refinance` hint direct-routes to that journey; everything else — a missing,
 * blank, or unrecognized value — resolves to the neutral selector.
 */
export function resolveEntryMode(raw: string | null | undefined): EntryMode {
  if (typeof raw !== "string") return "selector";
  const value = raw.trim().toLowerCase();
  if (value === "refinance") return "refinance-wizard";
  if (value === "purchase") return "purchase-wizard";
  return "selector";
}

/**
 * Resolves the entry mode from a URL search string (e.g. `"?loanPurpose=refinance"`).
 * Missing/blank/unknown values resolve to the neutral selector.
 */
export function resolveEntryModeFromSearch(search: string): EntryMode {
  const params = new URLSearchParams(search);
  return resolveEntryMode(params.get(LOAN_PURPOSE_QUERY_PARAM));
}

/**
 * True when a resolved loan-purpose value should route into the refinance
 * journey. Shared by the initial query decision and the prefill decision so both
 * entry paths behave identically: only an explicit refinance value routes to
 * refinance.
 */
export function shouldRouteToWizard(rawLoanPurpose: string | null | undefined): boolean {
  return resolveEntryMode(rawLoanPurpose) === "refinance-wizard";
}
